import { useSession } from './useSession';

/**
 * Returns true when the current session grants the given module (and optionally
 * a specific action on that module).
 *
 * Session shape:
 *   session.mobileModules  — string[] of MOB-* codes the user can see
 *   session.actions        — { moduleCode, actions: string[] }[]
 *
 * Usage:
 *   const canBook = useHasPermission('MOB-QBOOK', 'CREATE');
 *   const canSeeApprovals = useHasPermission('MOB-APRV');   // module-only check
 *
 * Fail-closed by design — returns false when session is null or the module /
 * action isn't explicitly granted. Never a permissive fallback.
 */
export function useHasPermission(moduleCode: string, action?: string): boolean {
  const { session } = useSession();
  if (!session) return false;
  if (!session.mobileModules.includes(moduleCode)) return false;
  if (!action) return true;

  const modActions = session.actions.find((a) => a.moduleCode === moduleCode);
  if (!modActions) return false;
  const wanted = action.toUpperCase();
  return modActions.actions.some((a) => a.toUpperCase() === wanted);
}
